<template>
  <Container
    id="heading"
  >
    <HeroCard
      :heading="t('jc.view.editor.hero.title')"
      :paragraph="t('jc.view.editor.hero.text')"
      :image="{
        src: 'images/component/participateCallCard/background.png',
        alt: t('jc.view.editor.hero.banner.alt'),
      }"
      :float="{
        src: 'images/skin_renders/roles/editor.png',
        alt: t('jc.view.editor.hero.float.alt'),
      }"
      :label="t('jc.view.editor.hero.button.label')"
      :icon="{ prefix: 'fab', iconName: 'discord' }"
      :to="config.public.discordParticipateUrl"
    />
  </Container>

  <Container
    id="our-offer"
  >
    <ContentCard
      :heading="t('jc.view.editor.ourOffer.title')"
      :paragraph="t('jc.view.editor.ourOffer.text')"
    >
      <IconWidget
        :items="ourOfferHighlightItems"
        class="my-12"
      />
      <IconList
        :items="ourOfferItems"
      />
    </ContentCard>
  </Container>

  <Container
    id="our-conditions"
  >
    <ContentCard
      :heading="t('jc.view.editor.ourConditions.title')"
      :paragraph="t('jc.view.editor.ourConditions.text')"
    >
      <IconList
        :items="ourConditionsItems"
      />
    </ContentCard>
  </Container>

  <Container
    id="your-tasks"
  >
    <ContentCard
      :heading="t('jc.view.editor.yourTasks.title')"
      :paragraph="t('jc.view.editor.yourTasks.text')"
    >
      <IconList
        :items="yourTasksItems"
      />
    </ContentCard>
  </Container>

  <Container
    id="impressions"
  >
    <ContentCard
      :heading="t('jc.view.editor.impressions.title')"
      :paragraph="t('jc.view.editor.impressions.text')"
    >
      <div
        class="grid grid-cols-1 laptop:grid-cols-2 gap-x-4 gap-y-8"
      >
        <template
          v-for="({ identifier, ...resource }, index) in impressionsResources"
          :key="index"
        >
          <DynamicResource
            :resource="resource"
            :caption="t(`jc.view.editor.impressions.${identifier}.text`)"
            aspect-video
            rounded
          />
        </template>
      </div>
    </ContentCard>
  </Container>

  <Container
    id="how-to-apply"
  >
    <ContentCard
      :heading="t('jc.view.editor.howToApply.title')"
      :paragraph="t('jc.view.editor.howToApply.text')"
    >
      <IconSection
        :sections="howToApplySections"
      />
    </ContentCard>
  </Container>

  <Container
    id="more-information"
  >
    <div
      class="flex flex-col gap-8"
    >
      <ContentCard
        :heading="t('jc.view.editor.moreInformation.title')"
        :paragraph="t('jc.view.editor.moreInformation.text')"
      />
      <div
        class="grid grid-cols-1 mobile:gird-cols-2 laptop:grid-cols-3 desktop:grid-cols-4 gap-x-4 gap-y-16 justify-center"
      >
        <template
          v-for="(card, index) in moreInformationCards"
          :key="index"
        >
          <ShowcaseCard
            v-bind="card"
          />
        </template>
      </div>
    </div>
  </Container>

  <Container
    id="callToAction"
    :background="'white'"
  >
    <ParticipateCallCard />
  </Container>
</template>

<script setup lang="ts">
definePageMeta({
  nameKey: 'participate-editor',
})

const { t } = useI18n()
const localePath = useLocalePath()
const config = useRuntimeConfig()

const ourOfferHighlightItems = [
  {
    icon: 'users',
    text: t('jc.view.editor.ourOffer.iconHighlight.atmosphere.text'),
  },
  {
    icon: 'dragon',
    text: t('jc.view.editor.ourOffer.iconHighlight.creativeDevelopment.text'),
  },
  {
    icon: 'flask-round-potion',
    text: t('jc.view.editor.ourOffer.iconHighlight.innovativeConcepts.text'),
  },
  {
    icon: 'globe',
    text: t('jc.view.editor.ourOffer.iconHighlight.website.text'),
  },
  {
    icon: 'clock',
    text: t('jc.view.editor.ourOffer.iconHighlight.timeManagement.text'),
  },
  {
    icon: 'campground',
    text: t('jc.view.editor.ourOffer.iconHighlight.teamEvents.text'),
  },
]

const ourOfferItems = [
  {
    icon: 'shield-check',
    text: t('jc.view.editor.ourOffer.icon.securities.text'),
  },
  {
    icon: 'brain-arrow-curved-right',
    text: t('jc.view.editor.ourOffer.icon.coDecide.text'),
  },
  {
    icon: 'sitemap',
    text: t('jc.view.editor.ourOffer.icon.hierarchy.text'),
  },
  {
    icon: 'file-certificate',
    text: t('jc.view.editor.ourOffer.icon.licences.text'),
  },
  {
    icon: 'toolbox',
    text: t('jc.view.editor.ourOffer.icon.teamTools.text'),
  },
  {
    icon: 'paintbrush-pencil',
    text: t('jc.view.editor.ourOffer.icon.passionatePeople.text'),
  },
  {
    icon: 'industry-windows',
    text: t('jc.view.editor.ourOffer.icon.regularContent.text'),
  },
  {
    icon: 'fingerprint',
    text: t('jc.view.editor.ourOffer.icon.corporateIdentity.text'),
  },
  {
    icon: 'gears',
    text: t('jc.view.editor.ourOffer.icon.supportedWorkflows.text'),
  },
  {
    icon: 'comments',
    text: t('jc.view.editor.ourOffer.icon.exchange.text'),
  },
]

const ourConditionsItems = [
  {
    icon: 'couch',
    text: t('jc.view.editor.ourConditions.icon.teamSpirit.text'),
  },
  {
    icon: 'alicorn',
    text: t('jc.view.editor.ourConditions.icon.fantasy.text'),
  },
  {
    icon: 'book-open-reader',
    text: t('jc.view.editor.ourConditions.icon.learn.text'),
  },
  {
    icon: 'gauge-max',
    text: t('jc.view.editor.ourConditions.icon.motivation.text'),
  },
  {
    icon: 'comment-smile',
    text: t('jc.view.editor.ourConditions.icon.communication.text'),
  },
  {
    icon: 'battery-full',
    text: t('jc.view.editor.ourConditions.icon.endurance.text'),
  },
]

const yourTasksItems = [
  {
    icon: 'pen',
    text: t('jc.view.editor.yourTasks.icon.write.text'),
  },
  {
    icon: 'newspaper',
    text: t('jc.view.editor.yourTasks.icon.organise.text'),
  },
  {
    icon: 'list-dropdown',
    text: t('jc.view.editor.yourTasks.icon.create.text'),
  },
  {
    icon: 'list-ul',
    text: t('jc.view.editor.yourTasks.icon.design.text'),
  },
  {
    icon: 'stamp',
    text: t('jc.view.editor.yourTasks.icon.shape.text'),
  },
  {
    icon: 'comment-pen',
    text: t('jc.view.editor.yourTasks.icon.think.text'),
  },
]

const impressionsResources = [
  {
    identifier: 'hub',
    mediaType: 'image/*',
    src: 'images/impressions/general/hub.png',
  },
  {
    identifier: 'clashlandsHub',
    mediaType: 'image/*',
    src: 'images/impressions/general/pvp_hub.png',
  },
  {
    identifier: 'dungeonRoom',
    mediaType: 'image/*',
    src: 'images/impressions/general/dungeon_room.png',
  },
  {
    identifier: 'conquestMap',
    mediaType: 'image/*',
    src: 'images/impressions/general/conquest_map.png',
  },
  {
    identifier: 'cosmeticMenu',
    mediaType: 'image/*',
    src: 'images/impressions/general/cosmetic_menu.png',
  },
  {
    identifier: 'clashlandsCannon',
    mediaType: 'image/*',
    src: 'images/impressions/general/cannon.png',
  },
  {
    identifier: 'editNews',
    mediaType: 'image/*',
    src: 'images/impressions/editor/edit_news.png',
  },
  {
    identifier: 'tutorial',
    mediaType: 'image/*',
    src: 'images/impressions/editor/tutorial_menu.png',
  },
]

const howToApplySections = [
  {
    icon: 'hand-wave',
    title: t('jc.view.editor.howToApply.icon.firstContact.title'),
    text: t('jc.view.editor.howToApply.icon.firstContact.text'),
  },
  {
    icon: 'comments',
    title: t('jc.view.editor.howToApply.icon.conversation.title'),
    text: t('jc.view.editor.howToApply.icon.conversation.text'),
  },
  {
    icon: 'envelope',
    title: t('jc.view.editor.howToApply.icon.wait.title'),
    text: t('jc.view.editor.howToApply.icon.wait.text'),
  },
  {
    icon: 'user-graduate',
    title: t('jc.view.editor.howToApply.icon.trainee.title'),
    text: t('jc.view.editor.howToApply.icon.trainee.text'),
  },
  {
    icon: 'comment-smile',
    title: t('jc.view.editor.howToApply.icon.feedbackTalk.title'),
    text: t('jc.view.editor.howToApply.icon.feedbackTalk.text'),
  },
]

const moreInformationCards = [
  {
    icon: 'users',
    text: t('jc.view.editor.moreInformation.card.team.text'),
    label: t('jc.view.editor.moreInformation.card.team.button.label'),
    to: localePath({ name: 'team' }),
  },
  {
    icon: 'newspaper',
    text: t('jc.view.editor.moreInformation.card.weekly_review.text'),
    label: t('jc.view.editor.moreInformation.card.weekly_review.button.label'),
    to: localePath({ name: 'news' }),
  },
  {
    icon: 'signs-post',
    text: t('jc.view.editor.moreInformation.card.plan.text'),
    label: t('jc.view.editor.moreInformation.card.plan.button.label'),
    to: localePath({ name: 'game' }),
  },
  {
    icon: 'microchip',
    text: t('jc.view.editor.moreInformation.card.stackShare.text'),
    label: t('jc.view.editor.moreInformation.card.stackShare.button.label'),
    to: config.public.stackShareUrl,
  },
  {
    icon: 'cube',
    text: t('jc.view.editor.moreInformation.card.planetMinecraft.text'),
    label: t('jc.view.editor.moreInformation.card.planetMinecraft.button.label'),
    to: config.public.planetMinecraftUrl,
  },
  {
    icon: 'file-lines',
    text: t('jc.view.editor.moreInformation.card.blog.text'),
    label: t('jc.view.editor.moreInformation.card.blog.button.label'),
    to: config.public.scrayosUrl,
  },
]
</script>
